import type { StaticPageAudience, StaticPageKey } from './staticPageContent.js';
import { action, card, faq, section, type LocaleWithoutGerman, type LocalizedPageCopy } from './staticPageLocalizationModel.js';
import { commonPages } from './staticPageCommonLocalization.js';
import { globusCompanyPages } from './staticPageGlobusCompanyLocalization.js';

export const audiencePages: Record<
  LocaleWithoutGerman,
  Record<StaticPageAudience, Partial<Record<StaticPageKey, LocalizedPageCopy>>>
> = {
  tr: {
    applicant: {
      unternehmen: {
        eyebrow: 'İşverenleri tanı',
        title: 'Gerçekten yerleşebileceğin işverenleri tanı.',
        intro:
          'Bu sayfada Viva Porta’nın uygun klinikleri, bakım kurumlarını ve sağlık şirketlerini nasıl seçtiğini gösteriyoruz. Amaç yalnızca bir sözleşme değil; Almanya’daki başlangıcına mesleki, insani ve organizasyonel olarak uyan bir iş yeridir.',
        cards: [
          card(
            'Uygun kurumlar',
            'Uluslararası bakım çalışanlarını iyi karşılayabilecek işverenleri ekip yapısı, vardiya planı, onboarding, konut ve denklik deneyimi açısından değerlendiririz.',
          ),
          card(
            'Şeffaf sözleşme',
            'Nerede çalışacağını, hangi görevleri üstleneceğini ve koşulları erkenden öğrenirsin. Belirsiz vaatler yerine anlaşılır sonraki adımlar alırsın.',
          ),
          card(
            'Hazırlıklı görüşmeler',
            'Alman işverenlerle görüşmelere hazırlanır, tipik soruları açıklar ve deneyimini güvenle sunmana yardımcı oluruz.',
          ),
          card(
            'Ekip uyumu önemlidir',
            'İyi bir başlangıç yalnızca uzmanlık alanına bağlı değildir. Beklentilerin, iletişimin ve kişisel hedeflerin işverenle uyumuna bakarız.',
          ),
          card(
            'Günlük hayata yerleşmek',
            'Konut, kayıt, ilk yollar ve yerel yönelim hesaba katılır; böylece iş teklifi Almanya’da gerçekçi bir başlangıca dönüşür.',
          ),
          card(
            'Başlangıç sonrası destek',
            'İlk iş gününden sonra da ekip, kurumlar veya günlük hayatla ilgili sorular için ulaşılabilir kalırız.',
          ),
        ],
      },
      pflege: {
        cards: [
          card(
            'Bakım uzmanları',
            'Viva Porta nitelikli bakım çalışanlarını uzun vadeli ve adil plan yapan kurumlarla buluşturur.',
          ),
          card('Dil ve denklik', 'Almanya yolu dil hazırlığı, belgeler ve denklik adımlarıyla birlikte desteklenir.'),
          card('İnsani destek', 'Adaylar ve kurumlar sabit irtibat kişileri ve açık yönlendirme alır.'),
        ],
        cta: {
          eyebrow: 'Bakım başvurusu',
          title: 'Yoluna dürüst bir değerlendirmeyle başla.',
          text: 'Dil, denklik ve işveren görüşmeleri için hangi adımların uygun olduğunu birlikte değerlendirelim.',
          primaryAction: action('Şimdi başvur'),
          secondaryAction: action('Süreci incele'),
        },
      },
      handwerk: {
        cards: [
          card('Nitelik', 'Pratik deneyim ve eğitim yolları dikkatle değerlendirilir.'),
          card('İşletme ve insan', 'Küçük ekiplerde uyum, güven ve iki taraf için açık beklentiler gerekir.'),
          card('Yerleşme', 'Mesleki başlangıç günlük hayat, konut ve entegrasyonla birlikte düşünülür.'),
        ],
        cta: {
          eyebrow: 'Zanaat başvurusu',
          title: 'Niteliğinin Almanya’daki karşılığını öğren.',
          text: 'Deneyimini, belgelerini, dilini ve uzun vadeli plan yapan işletmeleri birlikte değerlendiririz.',
          primaryAction: action('Şimdi başvur'),
          secondaryAction: action('Süreci incele'),
        },
      },
      einzelhandel: {
        cards: [
          card(
            'Mağazalar ve lokasyonlar',
            'İhtiyaç, çalışma saatleri ve müşteri teması her lokasyonda farklıdır ve önceden netleştirilir.',
          ),
          card('Hizmet', 'Satışta dil ve kişisel hazırlık özellikle önemlidir.'),
          card('Uzun vadeli başlangıç', 'Amaç hem mesleki hem özel hayatta sürdürülebilir bir başlangıçtır.'),
        ],
        sections: commonPages.tr.globus?.sections?.slice(2),
        cta: {
          eyebrow: 'Perakende başvurusu',
          title: 'Perakendedeki başlangıcını hazırla.',
          text: 'Müşteri iletişimini, dili, lokasyonu ve günlük hayatına uyan rolleri birlikte netleştiririz.',
          primaryAction: action('Şimdi başvur'),
          secondaryAction: action('Süreci incele'),
        },
      },
      ablauf: {
        eyebrow: 'Adaylar için süreç',
        title: 'İşe başlayana kadar net bir yol.',
        intro:
          'Viva Porta başvuru, görüşmeler, dil, denklik, vize, ülkeye giriş ve Almanya’daki başlangıç sürecinde sana kişisel olarak eşlik eder.',
        cards: [
          card(
            'Tanışma ve hedef',
            'Eğitimini, deneyimini, hedefini ve mevcut Almanca seviyeni öğrenir; sana dürüst bir yol haritası sunarız.',
          ),
          card(
            'Belgeler ve profil',
            'Diploma, sertifika ve belgelerini kontrol eder, profilini uygun işverenler için hazırlarız.',
          ),
          card('Uygun seçim', 'Deneyimine, hedeflerine ve yaşam planına uyan rolleri birlikte değerlendiririz.'),
          card(
            'Ortak görüşmeler',
            'Görüşmelere hazırlanır, konuşmaları destekler ve sonrasında açık geri bildirim veririz.',
          ),
          card('İş sözleşmesi', 'Karardan sonra sözleşme ve zaman planı iki taraf için güven ve erken yönelim sağlar.'),
          card(
            'Dil ve denklik',
            'Almanca, mesleki dil, çeviriler ve denklik adımları hedef başlangıç tarihine göre planlanır.',
          ),
          card(
            'Resmî işlemler ve giriş',
            'Vize, çalışma izni, yolculuk ve ilk konaklama güvenli bir başlangıç için koordine edilir.',
          ),
          card(
            'Entegrasyon desteği',
            'Varıştan sonra günlük hayat, ekip ve kurumlarla ilgili sorular için yanında kalırız.',
          ),
        ],
      },
    },
    company: {
      unternehmen: {
        eyebrow: 'Şirketler için',
        title: 'Ekibinizi bürokrasiye boğmadan uluslararası bakım uzmanları kazanın.',
        intro:
          'Viva Porta kliniklerin, bakım kurumlarının ve sağlık şirketlerinin uluslararası ekiplerini planlı biçimde kurmasına yardımcı olur. Uygun uzmanları bulur, nitelik ve motivasyonu kontrol eder, dil, denklik, vize ve girişi yönetir ve günlük işe entegrasyonu destekleriz.',
        cards: [
          card(
            'İhtiyacı net anlamak',
            'Uzmanlık alanları, vardiyalar, başlangıç tarihleri, ücret çerçevesi, konut ve entegrasyon kapasitesiyle gerçek personel durumunuzdan başlarız.',
          ),
          card(
            'Ön değerlendirilmiş adaylar',
            'Yalnızca mesleki, dilsel ve kişisel olarak kontrol edilmiş profiller alırsınız. Eğitim, deneyim, motivasyon, Almanca ve denklik durumu belgelenir.',
          ),
          card(
            'Denklik ve kurumlar',
            'Belgeleri, çevirileri, tasdikleri, denklik başvurusunu, vizeyi ve çalışma iznini koordine ederiz. İK ekibiniz bilgilendirilir fakat her adımı tek başına takip etmek zorunda kalmaz.',
          ),
          card(
            'Planlanabilir aday hattı',
            'Uluslararası işe alım hazırlık gerektirir. Açık kilometre taşları, durum güncellemeleri ve gerçekçi zaman aralıklarıyla çalışırız.',
          ),
          card(
            'Entegrasyon dahil',
            'Konut, varış, resmî işlemler, ekip hazırlığı ve kültürel yönelim uzun vadeli başarıyı belirler. Bu aşamayı aktif biçimde destekleriz.',
          ),
          card(
            'Sabit irtibat kişileri',
            'Alman bakım işini ve Türkiye’deki aday tarafını anlayan bir ekiple çalışırsınız. Bu, sürtünmeyi azaltır ve iki tarafta güven yaratır.',
          ),
        ],
      },
      pflege: {
        cards: [
          card(
            'Mesleki ön değerlendirme',
            'Eğitim, deneyim, motivasyon ve denklik olasılığı aday sunulmadan önce izlenebilir biçimde kontrol edilir.',
          ),
          card(
            'Mesleğe yönelik dil',
            'Hazırlık yalnızca sınavlara değil, bakım günlüklerindeki iletişime de odaklanır.',
          ),
          card(
            'Denklik desteği',
            'Belgeler, çeviriler, denklik başvurusu, vize ve çalışma izni yapılandırılmış biçimde koordine edilir.',
          ),
        ],
        sections: [
          section(
            'Zorluk',
            'Bakım kısa vadeli doluluk değil, güvenilirlik ister.',
            'Açık pozisyonlar kalıcı olduğunda vardiyalar, bakım kalitesi ve ekip istikrarı baskı altına girer. Denklik, dil, onboarding ve kalma perspektifi uluslararası kazanımın sürdürülebilir olup olmayacağını belirler.',
            [
              card(
                'Planlama baskısı',
                'Eksikler ve açık pozisyonlar vardiya planını, ekipleri ve bakım kalitesini zorlar.',
              ),
              card('Resmî engeller', 'Denklik ve kurum iletişimi erken ve doğru hazırlanmalıdır.'),
              card(
                'Entegrasyon',
                'İyi başlangıç günlük hayatta belirlenir: konut, ekip iletişimi, onboarding ve destek.',
              ),
            ],
          ),
          section(
            'Viva Porta çözümü',
            'Seçimden ekibe varışa kadar yapılandırılmış bir yol.',
            'Viva Porta uluslararası işe alımı kişisel destekle birleştirir. Kurumlar uygun profiller, açık durum güncellemeleri ve zaman alan süreç adımlarında yardım alır.',
          ),
        ],
        faq: [
          faq(
            'Bakım alanında işe yerleştirme ne kadar sürer?',
            'Dil, denklik, vize ve başlangıç tarihi belirleyicidir. Bu nedenle kısa vadeli vaatler yerine gerçekçi kilometre taşlarıyla çalışırız.',
          ),
          faq(
            'Denklik nasıl bir rol oynar?',
            'Nitelikli çalışma için merkezîdir. Belgeleri erken kontrol eder, çevirileri koordine eder ve başvuruyu güvenilir bir karara kadar destekleriz.',
          ),
        ],
        cta: {
          eyebrow: 'Bakım personeli ihtiyacı',
          title: 'İhtiyacınızı gerçekçi biçimde değerlendirelim.',
          text: 'Kurumunuza hangi profillerin uyduğunu ve sürdürülebilir işe alım için hangi adımların gerekli olduğunu birlikte inceleyelim.',
          primaryAction: action('İhtiyaç bildir'),
          secondaryAction: action('Süreci incele'),
        },
      },
      handwerk: {
        cards: [
          card(
            'Niteliği değerlendirmek',
            'Mesleki deneyim, eğitim yolları ve pratik çalışma kapasitesi önceden değerlendirilir.',
          ),
          card(
            'İşletmeye uygun',
            'Küçük ve orta ölçekli işletmeler mesleki ve insani olarak ekibe uyan insanlara ihtiyaç duyar.',
          ),
          card(
            'Yerleşme desteği',
            'Konut, kurumlar ve günlük hayat başlangıcın organizasyonda takılmaması için hesaba katılır.',
          ),
        ],
        sections: [
          section(
            'Zorluk',
            'Zanaat genç yeteneklere, deneyime ve kalıcı insanlara ihtiyaç duyuyor.',
            'Siparişler, müşteri bağlılığı ve işletme devamlılığı nitelikli çalışanlara bağlıdır. Uluslararası uzmanlar; nitelik, dil ve entegrasyon gerçekçi planlandığında çözüm olabilir.',
            [
              card('Günlük darboğazlar', 'Eksik uzmanlar randevuları, projeleri ve büyümeyi yavaşlatır.'),
              card('Nitelik', 'Eğitim sistemleri farklıdır; bu nedenle dikkatli değerlendirme ve açık roller gerekir.'),
              card('Ekip yakınlığı', 'Özellikle zanaatte küçük ekipte ve müşteri randevularında güven önemlidir.'),
            ],
          ),
          section(
            'Viva Porta çözümü',
            'İşletme, uzman ve Almanya’daki günlük hayat arasında bir köprü.',
            'Viva Porta ön seçim, belgeler, dil ve entegrasyon planında destek olur. Amaç hızlı bir özgeçmiş değil, uzun vadeli düşünen bir işletmede gerçekçi başlangıçtır.',
          ),
        ],
        faq: [
          faq(
            'Hangi zanaat dalları için uygundur?',
            'Açık tanımlanmış rolleri ve planlanabilir ihtiyacı olan işletmeler için uygundur. Kesin uygunluk nitelik, denklik gereksinimi ve görev profiline bağlıdır.',
          ),
          faq(
            'Ekip uyumu nasıl değerlendirilir?',
            'Mesleki belgelerin yanında motivasyon, beklenti, dil seviyesi ve Almanya’da uzun vadeli yaşama isteği değerlendirilir.',
          ),
        ],
        cta: {
          eyebrow: 'Zanaat personeli ihtiyacı',
          title: 'İşletmenizi ve uygun profilleri konuşalım.',
          text: 'Hangi niteliklerin gerektiğini ve uluslararası uzman kazanımı için hangi yolun gerçekçi olduğunu birlikte değerlendirelim.',
          primaryAction: action('İhtiyaç bildir'),
          secondaryAction: action('Süreci incele'),
        },
      },
      einzelhandel: {
        cards: [
          card(
            'Lokasyon ihtiyacını anlamak',
            'Lokasyon, açılış saatleri, ürün çeşitliliği, müşteri teması ve ekip yapısı aramada dikkate alınır.',
          ),
          card(
            'Müşteri temasına hazırlık',
            'Hizmet ve satış rollerinde dil, tutum ve beklentiler özellikle dikkatle değerlendirilir.',
          ),
          card(
            'Sürdürülebilir işe alım',
            'Amaç lokasyonu, rolü ve günlük hayatı anlayan ve uzun vadede kalabilecek çalışanlardır.',
          ),
        ],
        sections: [
          section(
            'Zorluk',
            'Perakende güvenilirliği müşteri yakınlığıyla birleştiren insanlara ihtiyaç duyar.',
            'Vardiyalar, uzun açılış saatleri, lokasyona bağlılık ve değişken talep personel planlamasını zorlaştırır. Başlangıç açık ve gerçekçi hazırlanırsa uluslararası çalışanlar destek olabilir.',
            [
              card('Açılış saatleri', 'Uzun mağaza saatleri ve hafta sonları istikrarlı ekipler gerektirir.'),
              card('Personel değişimi', 'Sık değişim müşteri temasında zaman, bilgi ve kalite kaybına yol açar.'),
              card('Lokasyona bağlılık', 'İş yeri konut, günlük hayat ve sosyal yerleşimle de uyumlu olmalıdır.'),
            ],
          ),
          section(
            'Viva Porta çözümü',
            'Kısa vadeli destek yerine sürdürülebilir personel kazanımı.',
            'Gerçekten hangi rollerin gerektiğini, dil ve hizmet beklentilerini ve yeni çalışanların lokasyona nasıl iyi yerleşebileceğini erkenden netleştiririz.',
          ),
          ...(commonPages.tr.globus?.sections?.slice(2) ?? []),
          section(
            'Taze ürün alanları',
            'Taze ürün reyonları yalnızca personel değildir: ziyaret, danışmanlık ve alışveriş sepetini şekillendirir.',
            'Et, balık ve peynir reyonları büyük marketler için stratejik temas noktalarıdır. Nitelikli çalışanlar eksik olduğunda açılış saatleri, danışmanlık, ek satışlar ve indirim marketlerinden farklılaşma baskı altına girer.',
            [
              card(
                'Ziyaret ve bağlılık',
                'Güçlü taze ürün alanları müşterileri mağazaya çeker ve kişisel danışmanlık sunar.',
              ),
              card(
                'Marj ve ek satışlar',
                'Yetkin hizmet premium alışverişleri, anlık kararları ve uygun ek ürünleri destekler.',
              ),
              card(
                'Hijyen ve ürün güvenliği',
                'Hizmet yoğun alanlarda temiz çalışma, ürün kullanımı, güvenilirlik ve ekip iletişimi önemlidir.',
              ),
            ],
          ),
        ],
        faq: [
          faq(
            'Perakendede hangi roller uygundur?',
            'Satış, hizmet, depo ve destek rolleri; görevler, dil ve onboarding açık tanımlandığında uygun olabilir.',
          ),
          faq(
            'Uzun vadeli bağlılık nasıl desteklenir?',
            'Gerçekçi beklentiler, kişisel rehberlik ve günlük hayat, konut ve ekip iletişiminin erken netleştirilmesiyle.',
          ),
        ],
        cta: {
          eyebrow: 'Perakende personeli ihtiyacı',
          title: 'Lokasyonunuzu ve uygun rolleri konuşalım.',
          text: 'Personel ihtiyacınızı, dil ve hizmet beklentilerini ve gerçekçi zaman planını birlikte değerlendirelim.',
          primaryAction: action('İhtiyaç bildir'),
          secondaryAction: action('Globus iş birliğini incele'),
        },
      },
      globus: globusCompanyPages.tr,
      ablauf: {
        eyebrow: 'Şirketler için süreç',
        title: 'İhtiyaçtan işe başlangıca kadar planlı.',
        intro:
          'Viva Porta uluslararası personel kazanımını açık aşamalar, net sorumluluklar ve kişisel rehberlikle güvenilir biçimde yönetilebilir hâle getirir.',
        cards: [
          card(
            'Tanışma ve ihtiyaç',
            'Rolleri, sayıyı, niteliği, lokasyonu, başlangıç aralığını ve koşulları netleştiririz. Böylece gerçekçi bir arama profili oluşur.',
          ),
          card(
            'Aday havuzunu sunmak',
            'Uygun profiller, nitelikler, dil seviyesi, motivasyon ve olası görev alanları hakkında yapılandırılmış bir görünüm alırsınız.',
          ),
          card(
            'Uygun seçim',
            'Adayları belirler, belgeleri kontrol eder ve hızlı, anlaşılır kararlar için profilleri hazırlarız.',
          ),
          card(
            'Ortak görüşmeler',
            'Görüşmeler hazırlanır, yönetilir ve değerlendirilir. Motivasyon, iletişim, gelişim ve ekip uyumu açıkça sınıflandırılır.',
          ),
          card(
            'İş sözleşmesi',
            'Onaydan sonra sözleşme ve zaman planı iki taraf için bağlayıcılık ve erken yönelim sağlar.',
          ),
          card(
            'Dil ve denklik',
            'Dijital Almanca, mesleki dil, belgeler, çeviriler ve denklik hedef başlangıç tarihine göre koordine edilir.',
          ),
          card(
            'Kurumlar ve giriş',
            'Vize, çalışma izni, formaliteler, yolculuk ve ilk konaklama güvenilir bir başlangıç için uyumlandırılır.',
          ),
          card(
            'Entegrasyon desteği',
            'Varıştan sonra yönelim, ekip soruları ve günlük hayat başlangıç döneminde yakından desteklenir.',
          ),
        ],
      },
    },
  },
  en: {
    applicant: {
      unternehmen: {
        eyebrow: 'Understand employers',
        title: 'Meet employers where you can truly settle in.',
        intro:
          'This page explains how Viva Porta selects suitable clinics, care facilities and healthcare companies. You should receive more than just a contract: a workplace that fits your professional, personal and practical start in Germany.',
        cards: [
          card(
            'Suitable organisations',
            'We assess which employers can welcome international care professionals well, including team structure, shifts, onboarding, housing and recognition experience.',
          ),
          card(
            'Transparent contract',
            'You learn early where you would work, what your tasks are and which conditions apply. Clear next steps replace vague promises.',
          ),
          card(
            'Prepared interviews',
            'We prepare you for interviews with German employers, explain common questions and help you present your experience confidently.',
          ),
          card(
            'Team fit matters',
            'A good start depends on more than the department. We consider whether expectations, communication and personal goals fit the employer.',
          ),
          card(
            'Settling into daily life',
            'Housing, registration, first journeys and local orientation are considered so the job offer becomes a realistic start in Germany.',
          ),
          card(
            'Support after the start',
            'We remain available after the first working day when questions arise in the team, with authorities or in everyday life.',
          ),
        ],
      },
      pflege: {
        cards: [
          card(
            'Care professionals',
            'Viva Porta places qualified care professionals with organisations that plan fairly and for the long term.',
          ),
          card(
            'Language and recognition',
            'Your path to Germany includes guidance for language preparation, documents and recognition.',
          ),
          card('Personal guidance', 'Applicants and organisations receive fixed contacts and clear orientation.'),
        ],
        cta: {
          eyebrow: 'Care application',
          title: 'Start your path with an honest assessment.',
          text: 'Together we review the right steps for language, recognition and employer interviews.',
          primaryAction: action('Apply now'),
          secondaryAction: action('View process'),
        },
      },
      handwerk: {
        cards: [
          card('Qualifications', 'Practical experience and training paths are assessed carefully.'),
          card('Employer and person', 'Small teams need fit, trust and clear expectations on both sides.'),
          card('Arrival', 'The professional start is planned together with everyday life, housing and integration.'),
        ],
        cta: {
          eyebrow: 'Trades application',
          title: 'Understand how your qualification fits Germany.',
          text: 'We review experience, documents, language and employers that plan for the long term.',
          primaryAction: action('Apply now'),
          secondaryAction: action('View process'),
        },
      },
      einzelhandel: {
        cards: [
          card(
            'Stores and locations',
            'Demand, working hours and customer contact differ by location and are clarified in advance.',
          ),
          card('Service', 'Language and personal preparation are especially important in sales.'),
          card('Long-term start', 'The goal is a start that works professionally and personally.'),
        ],
        sections: commonPages.en.globus?.sections?.slice(2),
        cta: {
          eyebrow: 'Retail application',
          title: 'Prepare your start in retail.',
          text: 'We clarify customer contact, language, location and roles that fit your everyday life.',
          primaryAction: action('Apply now'),
          secondaryAction: action('View process'),
        },
      },
      ablauf: {
        eyebrow: 'Process for applicants',
        title: 'A clear path to your first day at work.',
        intro:
          'Viva Porta personally guides you through applications, interviews, language, recognition, visa, entry and your start in Germany.',
        cards: [
          card(
            'Introduction and goals',
            'We learn about your education, experience, goals and current German level and give you an honest roadmap.',
          ),
          card(
            'Documents and profile',
            'We review diplomas, certificates and documents and prepare your profile for suitable employers.',
          ),
          card(
            'Suitable selection',
            'Together we assess roles that fit your experience, goals and plans for life in Germany.',
          ),
          card('Joint interviews', 'We prepare you, support the conversations and provide clear feedback afterwards.'),
          card(
            'Employment contract',
            'After the decision, the contract and schedule create reliability and early orientation for both sides.',
          ),
          card(
            'Language and recognition',
            'German, professional language, translations and recognition steps are planned around the target start date.',
          ),
          card(
            'Authorities and entry',
            'Visa, work permit, travel and initial accommodation are coordinated for a reliable start.',
          ),
          card(
            'Integration support',
            'After arrival, we remain available for everyday, team and administrative questions.',
          ),
        ],
      },
    },
    company: {
      unternehmen: {
        eyebrow: 'For employers',
        title: 'Hire international care professionals without overloading your team with bureaucracy.',
        intro:
          'Viva Porta helps clinics, care facilities and healthcare companies build international teams in a planned way. We find suitable professionals, check qualifications and motivation, manage language, recognition, visa and entry, and support integration into everyday work.',
        cards: [
          card(
            'Understand demand precisely',
            'We begin with your actual staffing situation: departments, shifts, start dates, salary framework, housing and integration capacity.',
          ),
          card(
            'Prequalified candidates',
            'You only receive profiles assessed professionally, linguistically and personally. Training, experience, motivation, German progress and recognition potential are documented.',
          ),
          card(
            'Recognition and authorities',
            'We coordinate documents, translations, certifications, recognition, visa and work permits while keeping your HR team informed.',
          ),
          card(
            'Plannable pipeline',
            'International recruitment needs lead time. We work with clear milestones, status updates and realistic windows for workforce planning and onboarding.',
          ),
          card(
            'Integration included',
            'Housing, arrival, authorities, team preparation and cultural orientation determine long-term success. We actively support this phase.',
          ),
          card(
            'Fixed contacts',
            'You work with a team that understands German care operations and the Turkish candidate perspective, reducing friction and building trust.',
          ),
        ],
      },
      pflege: {
        cards: [
          card(
            'Professionally pre-screened',
            'Training, experience, motivation and recognition potential are checked transparently before a profile is presented.',
          ),
          card('Job-related language', 'Preparation covers not only exams but communication in everyday care work.'),
          card(
            'Recognition guided',
            'Documents, translations, recognition, visa and work permits are coordinated in a structured way.',
          ),
        ],
        sections: [
          section(
            'Challenge',
            'Care needs reliability, not short-term staffing.',
            'Shift planning, care quality and team stability come under pressure when vacancies remain open. Recognition, language, onboarding and retention determine whether international recruitment can truly last.',
            [
              card('Planning pressure', 'Absences and vacancies strain shift planning, teams and quality of care.'),
              card('Formal hurdles', 'Recognition and communication with authorities need early, careful preparation.'),
              card(
                'Integration',
                'A good start is decided in everyday life: housing, team communication, onboarding and guidance.',
              ),
            ],
          ),
          section(
            'Viva Porta solution',
            'A structured path from selection to arrival in the team.',
            'Viva Porta combines international recruitment with personal guidance. Organisations receive suitable profiles, clear status updates and support with time-consuming process steps.',
          ),
        ],
        faq: [
          faq(
            'How long does care recruitment take?',
            'Language, recognition, visa and the start date are decisive. We work with realistic milestones instead of short-term promises.',
          ),
          faq(
            'What role does recognition play?',
            'It is central to qualified employment. We review documents early, coordinate translations and guide the application to a reliable decision.',
          ),
        ],
        cta: {
          eyebrow: 'Care staffing needs',
          title: 'Let us assess your needs realistically.',
          text: 'Together we review which profiles fit your organisation and which steps support sustainable hiring.',
          primaryAction: action('Submit staffing need'),
          secondaryAction: action('View process'),
        },
      },
      handwerk: {
        cards: [
          card(
            'Assess qualifications',
            'Professional experience, training paths and practical capability are evaluated in advance.',
          ),
          card(
            'Fit for the business',
            'Small and medium-sized businesses need people who fit the team professionally and personally.',
          ),
          card(
            'Support arrival',
            'Housing, authorities and everyday life are considered so the start does not fail on practical organisation.',
          ),
        ],
        sections: [
          section(
            'Challenge',
            'Skilled trades need new talent, experience and people who stay.',
            'Orders, customer relationships and succession depend on qualified employees. International professionals can help when qualifications, language and integration are planned realistically.',
            [
              card('Everyday bottlenecks', 'Missing skilled workers slow schedules, projects and growth.'),
              card('Qualifications', 'Training systems differ, requiring careful assessment and clear roles.'),
              card('Close-knit teams', 'Trust matters especially in small trade teams and at customer appointments.'),
            ],
          ),
          section(
            'Viva Porta solution',
            'A bridge between the business, the professional and everyday life in Germany.',
            'Viva Porta supports pre-selection, documents, language and integration planning. The goal is not a quick CV, but a realistic start with a long-term employer.',
          ),
        ],
        faq: [
          faq(
            'Which trades are suitable?',
            'Generally, businesses with clearly defined roles and plannable demand. Suitability depends on qualifications, recognition requirements and the role profile.',
          ),
          faq(
            'How is team fit assessed?',
            'Alongside professional documents, we consider motivation, expectations, language level and the intention to build a long-term life in Germany.',
          ),
        ],
        cta: {
          eyebrow: 'Trades staffing needs',
          title: 'Let us discuss your business and suitable profiles.',
          text: 'Together we assess the required qualifications and a realistic path for international recruitment.',
          primaryAction: action('Submit staffing need'),
          secondaryAction: action('View process'),
        },
      },
      einzelhandel: {
        cards: [
          card(
            'Understand location needs',
            'Location, opening hours, range, customer contact and team structure are considered in the search.',
          ),
          card(
            'Prepare customer contact',
            'Language, presence and expectations receive particular attention for service and sales roles.',
          ),
          card(
            'Hire sustainably',
            'The goal is employees who understand the location, role and daily life and can stay long term.',
          ),
        ],
        sections: [
          section(
            'Challenge',
            'Retail needs people who combine reliability with customer focus.',
            'Shift models, extended opening hours, location requirements and changing demand make staffing difficult. International employees can help when their start is clearly guided and realistically prepared.',
            [
              card('Opening hours', 'Long store hours and weekends require stable teams.'),
              card('Turnover', 'Frequent changes cost time, knowledge and quality in customer contact.'),
              card('Location fit', 'The workplace also needs to fit housing, everyday life and social arrival.'),
            ],
          ),
          section(
            'Viva Porta solution',
            'Sustainable recruitment instead of short-term cover.',
            'We clarify early which roles are truly needed, which language and service expectations apply and how new employees can settle into the location.',
          ),
          ...(commonPages.en.globus?.sections?.slice(2) ?? []),
          section(
            'Fresh-food departments',
            'Fresh counters are more than staffing: they shape footfall, advice and basket value.',
            'Meat, fish and cheese counters are strategic contact points for full-range retailers. Missing qualified employees puts opening hours, advice, additional sales and differentiation under pressure.',
            [
              card(
                'Footfall and loyalty',
                'Strong fresh-food departments bring customers into the store and provide personal advice instead of self-service alone.',
              ),
              card(
                'Margin and additional sales',
                'Skilled service supports premium purchases, spontaneous choices and suitable complementary products.',
              ),
              card(
                'Hygiene and product safety',
                'Service-intensive areas depend on clean work, product handling, reliability and clear team communication.',
              ),
            ],
          ),
        ],
        faq: [
          faq(
            'Which retail roles are suitable?',
            'Sales, service, warehouse and supporting store roles may be suitable when tasks, language and onboarding are clearly defined.',
          ),
          faq(
            'How is long-term retention supported?',
            'Through realistic expectations, personal guidance and early clarification of everyday life, housing and team communication.',
          ),
        ],
        cta: {
          eyebrow: 'Retail staffing needs',
          title: 'Let us discuss your location and suitable roles.',
          text: 'Together we assess staffing demand, language and service expectations and a realistic timeline.',
          primaryAction: action('Submit staffing need'),
          secondaryAction: action('View Globus partnership'),
        },
      },
      globus: globusCompanyPages.en,
      ablauf: {
        eyebrow: 'Process for employers',
        title: 'Planned from staffing need to first day.',
        intro:
          'Viva Porta makes international recruitment reliably manageable through clear stages, defined responsibilities and personal guidance.',
        cards: [
          card(
            'Introduction and demand',
            'We clarify roles, numbers, qualifications, location, start window and conditions to create a realistic search profile.',
          ),
          card(
            'Present the talent pool',
            'You receive a structured view of suitable profiles, qualifications, language status, motivation and possible areas of work.',
          ),
          card(
            'Suitable selection',
            'We identify candidates, check documents and prepare profiles so decisions can be made quickly and transparently.',
          ),
          card(
            'Joint interviews',
            'Interviews are prepared, moderated and reviewed. Motivation, communication, development and team fit are assessed clearly.',
          ),
          card(
            'Employment contract',
            'After approval, the contract and schedule create commitment and early orientation for both sides.',
          ),
          card(
            'Language and recognition',
            'Digital German courses, professional language, documents, translations and recognition are coordinated around the target start.',
          ),
          card(
            'Authorities and entry',
            'Visa, work permit, formalities, travel and initial accommodation are aligned for a reliable start.',
          ),
          card(
            'Guide integration',
            'After arrival, orientation, team questions and everyday life receive close support during the starting phase.',
          ),
        ],
      },
    },
  },
};
